import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { getPatientNotes } from "../../../services/patient/details";
import { getLayerZIndex, getOverlayRoot } from "../../../utils/layerManager";
import { Dropdown, DropdownItem } from "../../ui/dropdown";
import NotesList from "./list";
import drawerStyles from "./NotesDrawer.module.css";
import notesStyles from "../../../styles/patient-page-modules/Notes.module.css";

// Duration options for the Notes History range dropdown. Values are the
// `duration` query param sent straight through to
// GET /patients/<tenant>/<location>/notes/?duration=<value>&sort=oldest —
// the backend applies the window, no client-side date filtering here.
const RANGE_OPTIONS = [
  { value: "1w", label: "1 Week" },
  { value: "1m", label: "1 Month" },
  { value: "3m", label: "3 Months" },
  { value: "6m", label: "6 Months" },
  { value: "1y", label: "1 Year" },
  { value: "2y", label: "2 Years" },
  { value: "3y", label: "3 Years" },
];
const DEFAULT_RANGE = "1m";

/** Range dropdown trigger — reuses the Notes panel's sort-pill styling so
 * it reads as a sibling of the inline/expanded Notes sort control. */
const RangeControls = ({ value, onChange }) => {
  const activeLabel =
    RANGE_OPTIONS.find((o) => o.value === value)?.label || "1 Month";

  return (
    <Dropdown
      options={RANGE_OPTIONS}
      value={value}
      onChange={onChange}
      trigger={
        <button type="button" className={notesStyles.sortTrigger}>
          <span>{activeLabel}</span>
          <svg
            className={notesStyles.sortChevron}
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      }
    >
      {RANGE_OPTIONS.map((opt) => (
        <DropdownItem key={opt.value} value={opt.value}>
          {opt.label}
        </DropdownItem>
      ))}
    </Dropdown>
  );
};

/**
 * NotesHistoryDrawer — right-edge panel listing a patient's notes over a
 * selectable date range (1 Week … 3 Years, default 1 Month).
 *
 * Distinct from <NotesDrawer>: that one exposes a Latest/Oldest sort +
 * RPM/CCM filters over ALL notes. This one always fetches oldest-first
 * and sends the selected range as the `duration` query param
 * (/patients/<tenant>/<location>/notes/?sort=oldest&duration=<1w|1m|3m|6m|1y|2y|3y>)
 * — the backend applies the date window, not a client-side filter.
 *
 * Reuses <NotesList> for row rendering (timeline dots, kebab Edit/Delete)
 * and the NotesDrawer chrome (backdrop, slide-in surface, portal target)
 * so it stays visually consistent with the existing Notes drawer.
 */
const NotesHistoryDrawer = ({
  isOpen,
  onClose,
  chatBotType,
  onNoteClick,
  patientId,
  locationId,
}) => {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const closeBtnRef = useRef(null);

  const {
    data: notes,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["patient-notes", patientId, "oldest", range],
    () => getPatientNotes(null, "oldest", patientId, locationId, false, range),
    {
      enabled: !!patientId && isOpen,
      staleTime: 2 * 60 * 1000,
    }
  );

  // Body scroll lock + Esc handler + initial focus.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  // Reset the range when the drawer closes so the next open starts back
  // at the default 1 Month window.
  useEffect(() => {
    if (!isOpen) setRange(DEFAULT_RANGE);
  }, [isOpen]);

  if (!isOpen) return null;

  const z = getLayerZIndex("drawer");

  return createPortal(
    <>
      <div
        className={drawerStyles.backdrop}
        style={{ zIndex: z }}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={drawerStyles.surface}
        style={{ zIndex: z + 1 }}
        role="dialog"
        aria-modal="true"
        aria-label="Notes History"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={drawerStyles.header}>
          <button
            type="button"
            ref={closeBtnRef}
            className={drawerStyles.collapseBtn}
            onClick={onClose}
            aria-label="Collapse Notes History drawer"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <h2 className={drawerStyles.title}>Notes History</h2>

          <div className={drawerStyles.spacer} />

          <RangeControls value={range} onChange={setRange} />
        </div>

        <div className={drawerStyles.body}>
          <NotesList
            notes={notes}
            isLoading={isLoading}
            error={error}
            refetch={refetch}
            onNoteClick={onNoteClick}
            chatBotType={chatBotType}
          />
        </div>
      </aside>
    </>,
    getOverlayRoot()
  );
};

export default NotesHistoryDrawer;
